'use client';

import {
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  InfiniteData,
  QueryKey,
} from '@tanstack/react-query';
import { ApiError, ApiFailedError } from '@utils/customErrors';
import { useEffect, useMemo, useState } from 'react';

// 커스텀 옵션 타입에서 Omit할 속성들
type OmittedKeys =
  | 'queryKey'
  | 'queryFn'
  | 'getNextPageParam'
  | 'initialPageParam';

interface QueryOptions<T>
  extends Omit<
    UseInfiniteQueryOptions<
      T,
      Error,
      InfiniteData<T, number>,
      QueryKey,
      number
    >,
    OmittedKeys
  > {
  queryKey: QueryKey;
  queryFn: ({ pageParam }: { pageParam: number }) => Promise<T>;
  onError?: (error: Error) => void;
  initialPageParam?: number;
  getPreviousPageParam?: (
    previousPage: T,
    allPages: T[],
    previousPageParam: number,
    allPageParams: number[]
  ) => number | undefined;
  getNextPageParam?: (
    lastPage: T,
    allPages: T[],
    lastPageParam: number,
    allPageParams: number[]
  ) => number | undefined;
  initialDataEnabled?: boolean;
}

export function useWckInfiniteQuery<T>(options: QueryOptions<T>) {
  const [isMounted, setIsMounted] = useState(false);
  const {
    queryKey,
    queryFn,
    initialDataEnabled = true,
    onError,
    initialPageParam = 1,
    getNextPageParam,
    ...restOptions
  } = options;

  const infiniteQueryKey = useMemo(
    () => ['wckQueryKey', ...queryKey],
    [queryKey]
  );

  useEffect(() => {
    if (isMounted) {
      const filteredQueryData = Object?.entries(
        (window?.history?.state?.filteredQueryData as Record<
          string,
          unknown
        >) || {}
      )
        .filter(item => item[0] !== infiniteQueryKey.join())
        .reduce(
          (acc, [key, value]) => ({
            ...acc,
            [key]: value,
          }),
          {}
        );
      window.history.replaceState(
        {
          ...window.history.state,
          filteredQueryData,
        },
        '',
        undefined
      );
    }
    setIsMounted(true);
  }, [isMounted]);

  const wrappedQueryFn = async ({ pageParam = initialPageParam }) => {
    try {
      return await queryFn({ pageParam });
    } catch (error) {
      const apiError = error as ApiFailedError;
      const customError = apiError;
      if (onError) {
        onError(customError);
      }
      throw customError;
    }
  };

  const infiniteInitialData = useMemo(() => {
    if (
      typeof window !== 'undefined' &&
      initialDataEnabled &&
      window?.history?.state?.filteredQueryData
    ) {
      const filteredQueryData = window?.history?.state?.filteredQueryData?.[
        infiniteQueryKey?.join()
      ] as InfiniteData<T, number> | undefined;
      return filteredQueryData;
    }
    return restOptions.initialData as InfiniteData<T, number>;
  }, [infiniteQueryKey, initialDataEnabled, restOptions.initialData]);

  const query = useInfiniteQuery<
    T,
    Error,
    InfiniteData<T, number>,
    QueryKey,
    number
  >({
    queryKey: infiniteQueryKey,
    queryFn: wrappedQueryFn,
    getNextPageParam: getNextPageParam || (() => undefined),
    initialPageParam,
    initialData: infiniteInitialData,
    ...restOptions,
  });

  return query;
}
